<script setup lang="ts">
// An encode's settings (codec, rates, picture, audio tracks; `part`
// "advanced": rates, level, scaling and extra ffmpeg arguments), shared by
// the encode editor (EditDrawer) and the encoding template editor
// (TemplatesDrawer), so the two never differ. `gpus` fills the NVENC GPU
// select (none: no select, a template does not carry the GPU); `ffopts`
// the extra arguments' autocomplete; `use` what the encode's destinations
// are (platforms or a contribution link) for the encode lab's guidance.
import { computed, ref } from 'vue'
import type { Audio, Video } from '../api'
import {
    hasLookahead,
    isLowDelay,
    isRecommended,
    lowDelay,
    lowDelayTuning,
    recommended,
    withoutLookahead,
    type Use,
} from '../encoding'
import { fpsLabel } from '../util'
import { verticalSizes } from '../vertical'
import ChannelSelect from './ChannelSelect.vue'

const video = defineModel<Video>('video', { required: true })
const audio = defineModel<Audio[]>('audio', { required: true })
const advanced = defineModel<string | undefined>('advanced')
const props = defineProps<{
    part: 'main' | 'advanced'
    gpus?: { index: number; name: string; sessions: number; nvenc_max: number; nvenc_no_limit?: boolean }[]
    ffopts?: { name: string; help: string }[]
    use?: Use | null
}>()
// The encode lab's guidance (DECISIONS 2026-10-09 "encode guidance"): the
// recommended settings are filled into the fields, never chosen for the
// operator. NVENC H.264 for platforms: p7, 3 B-frames, lookahead with fixed
// B-frames, temporal AQ.
const canNVENC = computed(() => props.gpus === undefined || props.gpus.length > 0) // a template: any node
const recommendedNow = computed(() => isRecommended(video.value, advanced.value))
function applyRecommended() {
    const r = recommended(video.value, advanced.value)
    video.value = r.video
    advanced.value = r.advanced
}
const removeLookahead = () => (advanced.value = withoutLookahead(advanced.value))
// a contribution link's low delay (DECISIONS 2026-10-10 "SRT latency"): no
// B-frames and the encoder's zero-latency tuning, offered, never set
const lowDelayNow = computed(() => isLowDelay(video.value, advanced.value))
const lowDelayFlags = computed(() => lowDelayTuning(video.value.codec).join(' '))
function applyLowDelay() {
    const r = lowDelay(video.value, advanced.value)
    video.value = r.video
    advanced.value = r.advanced
}
// the lab's rates for H.264 1080p59.94 (ENCODE-LAB-2026-10-09.md)
const rateHint =
    'From the encode lab, on hard esports gameplay: up to about 10 Mb/s the settings are worth 10-15 % of the bitrate; from about 16 Mb/s every setting but x264 veryfast looks excellent; above 20 Mb/s nothing visible is gained.'

const gpus = computed(() => props.gpus ?? [])
// a node without an NVIDIA GPU (gpus given and empty) greys out the NVENC
// codecs, as a DeckLink input without a card; a template (no node) offers all
const noNVIDIA = computed(() => props.gpus !== undefined && props.gpus.length === 0)

const codecs = ['libx264', 'h264_nvenc', 'libx265', 'hevc_nvenc', 'libsvtav1', 'av1_nvenc']
const layouts = ['mono', 'stereo', '5.1', '7.1']
const layoutChannels: Record<string, number> = { mono: 1, stereo: 2, '5.1': 6, '7.1': 8 }
// the profiles each encoder takes: the same lists as config.VideoProfiles
// on the node, which rejects anything else (AV1 encoders have none)
const profiles: Record<string, string[]> = {
    libx264: ['baseline', 'main', 'high', 'high10', 'high422'],
    libx265: ['main', 'main10', 'main12', 'main422-10'],
    h264_nvenc: ['baseline', 'main', 'high', 'high444p'],
    hevc_nvenc: ['main', 'main10', 'rext'],
}
const profilesFor = (codec: string) => profiles[codec] ?? []
const levelsFor = (codec: string) =>
    codec.includes('264')
        ? ['auto', '3.1', '4.0', '4.1', '4.2', '5.0', '5.1', '5.2', '6.0', '6.1']
        : codec.includes('265') || codec.includes('hevc')
          ? ['auto', '4', '4.1', '5', '5.1', '5.2', '6', '6.1']
          : ['auto']
const resolutions = ['', '1280x720', '1920x1080', '2560x1440', '3840x2160']
// a vertical encode's frames (vertical.ts), and a size saved that is in neither list
const sizes = computed(() => {
    const list = video.value.vertical ? verticalSizes : resolutions
    return resolution.value && !list.includes(resolution.value) ? [...list, resolution.value] : list
})
const frameRates = ['', '60000/1001', '60', '50', '30000/1001', '30', '25', '24000/1001', '24']
const pixFmts = [
    ['', '4:2:0 · 8 bit'],
    ['nv12', '4:2:0 · 8 bit (nv12)'],
    ['yuv422p', '4:2:2 · 8 bit'],
    ['p010le', '4:2:0 · 10 bit'],
    ['yuv422p10le', '4:2:2 · 10 bit'],
]
// what each encoder takes (config.encoderFormats on the node): 4:2:2 on
// x264 and x265 only, 10 bits on all but H.264 NVENC
const pixFmtsFor = (codec: string) =>
    pixFmts.filter(
        ([p]) =>
            (!p?.startsWith('yuv422') || codec === 'libx264' || codec === 'libx265') &&
            (p !== 'p010le' || codec !== 'h264_nvenc'),
    )
const tenBit = computed(() => ['p010le', 'yuv420p10le', 'yuv422p10le'].includes(video.value.pix_fmt ?? ''))
const hdr10 = computed(() => tenBit.value && (video.value.codec === 'libx265' || video.value.codec === 'libsvtav1'))
const resolution = computed({
    get: () => (video.value.width && video.value.height ? `${video.value.width}x${video.value.height}` : ''),
    set: (r: string) => {
        const [w, hh] = r.split('x').map(Number)
        video.value.width = w || undefined
        video.value.height = hh || undefined
    },
})
// a codec change drops a profile or preset the new encoder does not take
// (x264's "high" and "veryfast" stopped an HEVC NVENC encode)
function codecChanged() {
    const v = video.value
    if (v.profile && !profilesFor(v.codec).includes(v.profile)) v.profile = ''
    if (!pixFmtsFor(v.codec).some(([p]) => p === (v.pix_fmt ?? ''))) v.pix_fmt = ''
    if (v.preset && !presetsFor(v.codec).includes(v.preset))
        v.preset = presetsFor(v.codec)[Math.floor(presetsFor(v.codec).length / 2)] ?? ''
}
const presetsFor = (codec: string) =>
    codec.endsWith('nvenc')
        ? ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7']
        : codec === 'libsvtav1'
          ? ['4', '6', '8', '10', '12']
          : ['ultrafast', 'superfast', 'veryfast', 'faster', 'fast', 'medium', 'slow', 'slower', 'veryslow']

// what a preset means, since the names differ per encoder
const presetNotes: Record<string, Record<string, string>> = {
    nvenc: { p1: 'fastest, lowest quality', p4: 'balanced', p7: 'slowest, best quality' },
    libsvtav1: { '4': 'slow, best quality', '8': 'balanced', '12': 'fastest' },
    x26x: {
        ultrafast: 'lowest quality',
        veryfast: 'usual for live',
        medium: 'about NVENC p4',
        veryslow: 'best, most CPU',
    },
}
function presetLabel(codec: string, p: string) {
    const note = presetNotes[codec.endsWith('nvenc') ? 'nvenc' : codec === 'libsvtav1' ? 'libsvtav1' : 'x26x']?.[p]
    return note ? `${p} · ${note}` : p
}
const presetHint = (codec: string) =>
    codec.endsWith('nvenc')
        ? 'NVENC quality presets: p1 is fastest with the lowest quality, p7 slowest with the best. They cost GPU time, not CPU; p4-p5 suit several live 1080p encodes on one GPU.'
        : codec === 'libsvtav1'
          ? 'SVT-AV1: a lower number is slower and better; 8-10 for live.'
          : 'x264/x265: slower presets give better quality per bit at far more CPU: check that the node keeps up (dropped frames).'
// codecs whose running encoder takes a new bitrate without a restart
// (ffcmd.LiveBitrate; needs central's ffmpeg build)
const liveCodecs = ['libx264', 'h264_nvenc', 'hevc_nvenc', 'av1_nvenc']
const liveHint =
    'Changing only the bitrate, max rate or buffer of a running x264 or NVENC encode applies live: no restart, receivers stay connected. x265 and SVT-AV1 restart the encode.'
function blankTrack(i: number): Audio {
    return { name: '', codec: 'aac', bitrate_k: 128, layout: 'stereo', channel_map: [i * 2, i * 2 + 1] }
}
function setLayout(track: Audio, l: string) {
    track.layout = l
    const n = layoutChannels[l] ?? 2
    const first = track.channel_map[0] ?? 0
    track.channel_map = Array.from({ length: n }, (_, i) => track.channel_map[i] ?? first + i)
}
// ffmpeg option autocomplete for the raw-arguments field
const optQuery = ref('')
const optMatches = computed(() => {
    const q = optQuery.value.replace(/^-/, '').toLowerCase()
    return q.length < 2 ? [] : (props.ffopts ?? []).filter((o) => o.name.toLowerCase().includes(q)).slice(0, 8)
})
function addOpt(name: string) {
    advanced.value = ((advanced.value ?? '').trim() + ' -' + name + ' ').replace(/^ /, '')
    optQuery.value = ''
}
</script>

<template>
    <template v-if="part === 'main'">
        <div class="grid grid-cols-3 gap-2.5">
            <div class="mb-2.5">
                <label>Codec</label
                ><select v-model="video.codec" @change="codecChanged">
                    <option
                        v-for="c in codecs"
                        :key="c"
                        :value="c"
                        :disabled="noNVIDIA && c.endsWith('nvenc') && c !== video.codec"
                    >
                        {{ c }}{{ noNVIDIA && c.endsWith('nvenc') ? ' (no NVIDIA GPU)' : '' }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Preset</label
                ><select v-model="video.preset" :title="presetHint(video.codec)">
                    <option v-for="p in presetsFor(video.codec)" :key="p" :value="p">
                        {{ presetLabel(video.codec, p) }}
                    </option>
                </select>
            </div>
            <div v-if="profilesFor(video.codec).length" class="mb-2.5">
                <label>Profile</label
                ><select v-model="video.profile">
                    <option value="">default</option>
                    <option v-for="pr in profilesFor(video.codec)" :key="pr">{{ pr }}</option>
                </select>
            </div>
            <div v-if="video.codec.endsWith('nvenc') && props.gpus" class="mb-2.5">
                <label>GPU</label
                ><select v-model.number="video.gpu">
                    <option v-for="g in gpus" :key="g.index" :value="g.index">
                        {{ g.index }} · {{ g.name.replace(/^NVIDIA /, '') }} · {{ g.sessions
                        }}{{ g.nvenc_max ? '/' + g.nvenc_max + (g.nvenc_no_limit ? '+' : '') : '' }} NVENC
                    </option>
                    <option v-if="!gpus.some((g) => g.index === video.gpu)" :value="video.gpu">
                        {{ video.gpu }}{{ gpus.length ? ' (not in this node)' : ' (no GPU detected)' }}
                    </option>
                </select>
            </div>
        </div>
        <div class="mb-2.5 text-xs text-fg-3" data-test="encode-guidance">
            <template v-if="use?.use === 'contribution'">
                <p v-if="hasLookahead(advanced)" class="my-0.5">
                    {{ use.why }}: the lookahead adds about 0.5 s.
                    <button type="button" class="btn-ghost text-xs" @click="removeLookahead">
                        Remove the lookahead
                    </button>
                </p>
                <p v-if="lowDelayNow" class="my-0.5">
                    ✓ Low delay: no B-frames, <span class="font-mono">{{ lowDelayFlags }}</span
                    >.
                </p>
                <p v-else-if="lowDelayFlags" class="my-0.5">
                    {{ use.why }}. Lowest delay: no B-frames and <span class="font-mono">{{ lowDelayFlags }}</span
                    >, each frame out as it is made; a little less picture at the same rate.
                    <button type="button" class="btn-ghost text-xs" data-test="apply-low-delay" @click="applyLowDelay">
                        Apply low delay
                    </button>
                </p>
            </template>
            <template v-else>
                <p v-if="recommendedNow" class="my-0.5">
                    ✓ The encode lab's settings for platforms: p7, 3 B-frames, lookahead with fixed B-frames, temporal
                    AQ, key frames on the 2 s cadence only (about 0.5 s more delay).
                </p>
                <p v-else-if="video.codec === 'h264_nvenc'" class="my-0.5">
                    <template v-if="use">{{ use.why }}. </template>Recommended for platforms (encode lab): p7, 3
                    B-frames and
                    <span class="font-mono">-rc-lookahead 32 -b_adapt 0 -temporal-aq 1 -no-scenecut 1</span>, a better
                    picture for about 0.5 s more delay, no CPU.
                    <button
                        type="button"
                        class="btn-ghost text-xs"
                        data-test="apply-recommended"
                        @click="applyRecommended"
                    >
                        Apply
                    </button>
                </p>
                <p v-else-if="video.codec === 'libx264' && canNVENC && use" class="my-0.5">
                    <template v-if="use">{{ use.why }}. </template>NVENC with the encode lab's settings looks better
                    than x264 up to slow and costs no CPU.
                    <button
                        type="button"
                        class="btn-ghost text-xs"
                        data-test="apply-recommended"
                        @click="applyRecommended"
                    >
                        Use NVENC, recommended settings
                    </button>
                </p>
            </template>
        </div>
        <div class="grid grid-cols-3 gap-2.5">
            <div class="mb-2.5">
                <label>Rate control</label
                ><select v-model="video.rc">
                    <option>cbr</option>
                    <option>vbr</option>
                </select>
            </div>
            <div class="mb-2.5">
                <label :title="liveHint"
                    >Bitrate (kbit/s)<span v-if="liveCodecs.includes(video.codec)" class="text-fg-3">
                        · changes live</span
                    ></label
                ><input v-model="video.bitrate_k" type="number" min="100" :title="liveHint" />
            </div>
            <div class="mb-2.5"><label>GOP (frames)</label><input v-model="video.gop" type="number" min="1" /></div>
        </div>
        <p v-if="video.codec.includes('264')" class="mt-0 text-xs text-fg-3" :title="rateHint">
            H.264 1080p60: Twitch takes 8 Mb/s at most; hard gameplay looks excellent from about 16 Mb/s, and above 20
            nothing visible is gained.
        </p>
        <div class="grid grid-cols-3 gap-2.5">
            <div class="mb-2.5">
                <label>Resolution</label
                ><select v-model="resolution">
                    <option v-for="r in sizes" :key="r" :value="r">
                        {{ r ? r.replace('x', '×') : 'same as input' }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Frame rate</label
                ><select v-model="video.fps">
                    <option v-for="f in frameRates" :key="f" :value="f">
                        {{ f === '' ? 'same as input' : fpsLabel(f) }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Chroma · bit depth</label
                ><select v-model="video.pix_fmt">
                    <option v-for="[v, l] in pixFmtsFor(video.codec)" :key="v" :value="v">{{ l }}</option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Colour</label
                ><select v-model="video.colour">
                    <option value="">
                        {{ tenBit ? "auto · the input's (HDR kept)" : 'auto · SDR (HDR tonemapped)' }}
                    </option>
                    <option value="sdr">SDR · an HDR input tonemapped</option>
                    <option value="source">the input's · HDR kept{{ tenBit ? '' : ' in 8 bits' }}</option>
                </select>
            </div>
        </div>
        <p class="mt-0 text-xs text-fg-3">
            An HDR input (HLG, PQ) to an SDR encode is tonemapped to BT.709, which costs about 1.5 CPU cores at 1080p50
            (less when the encode scales down). For HDR on air, choose 10 bits with HEVC or AV1.
        </p>
        <div v-if="hdr10" class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>HDR10 mastering display (PQ)</label
                ><input
                    v-model="video.master_display"
                    placeholder="G(13250,34500)B(7500,3000)R(34000,16000)WP(15635,16450)L(10000000,1)"
                />
            </div>
            <div class="mb-2.5">
                <label>MaxCLL, MaxFALL (nits)</label><input v-model="video.max_cll" placeholder="1000,400" />
            </div>
        </div>
        <div class="mb-2.5">
            <label>Audio tracks</label>
            <div v-for="(a, ti) in audio" :key="ti" class="mb-2 rounded-md border border-line px-2.5 pt-2 pb-1.5">
                <div class="grid grid-cols-3 gap-2.5">
                    <div class="mb-2.5">
                        <label>Name</label><input v-model="a.name" :placeholder="ti === 0 ? 'program' : 'commentary'" />
                    </div>
                    <div class="mb-2.5">
                        <label>Layout</label
                        ><select :value="a.layout" @change="setLayout(a, ($event.target as HTMLSelectElement).value)">
                            <option v-for="l in layouts" :key="l">{{ l }}</option>
                        </select>
                    </div>
                    <div class="mb-2.5">
                        <label>Bitrate (kbit/s)</label><input v-model="a.bitrate_k" type="number" />
                    </div>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                    <span class="text-xs text-fg-3">from the input's channels →</span>
                    <div v-for="(_, i) in a.channel_map" :key="i" class="flex items-center gap-0.75">
                        <span class="text-xs text-fg-3">{{ i + 1 }}←</span
                        ><ChannelSelect v-model="a.channel_map[i]!" :aria-label="`track ${ti + 1} channel ${i + 1}`" />
                    </div>
                    <span class="min-w-0 flex-1" />
                    <button
                        v-if="audio.length > 1"
                        type="button"
                        class="btn-ghost text-xs"
                        @click="audio.splice(ti, 1)"
                    >
                        remove
                    </button>
                </div>
            </div>
            <button
                v-if="audio.length < 8"
                type="button"
                class="btn-ghost text-xs"
                @click="audio.push(blankTrack(audio.length))"
            >
                + track
            </button>
        </div>
    </template>
    <template v-else>
        <div class="grid grid-cols-3 gap-2.5">
            <div class="mb-2.5">
                <label>Max rate (k)</label><input v-model="video.maxrate_k" type="number" placeholder="= bitrate" />
            </div>
            <div class="mb-2.5">
                <label>Buffer size (k)</label
                ><input v-model="video.bufsize_k" type="number" placeholder="2× max rate" />
            </div>
            <div class="mb-2.5">
                <label>B-frames</label><input v-model="video.bframes" type="number" min="0" max="7" />
            </div>
            <div class="mb-2.5">
                <label>Level</label
                ><select v-model="video.level">
                    <option v-for="l in levelsFor(video.codec)" :key="l" :value="l === 'auto' ? '' : l">
                        {{ l }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Scaling</label
                ><select v-model="video.scale_algo">
                    <option value="">bicubic</option>
                    <option>lanczos</option>
                    <option>bilinear</option>
                    <option>spline</option>
                </select>
            </div>
        </div>
        <div class="mb-2.5">
            <label>Encoder tuning options (for example -tune, -x264-params; no files or URLs)</label
            ><input
                v-model="advanced"
                class="font-mono tabular-nums"
                placeholder="-tune zerolatency -x264-params keyint_min=1"
            />
            <div class="mt-1.5 flex items-center items-start gap-2">
                <div class="relative flex-1">
                    <input v-model="optQuery" placeholder="find an ffmpeg option to add…" />
                    <div
                        v-if="optMatches.length"
                        class="absolute inset-x-0 top-full z-5 max-h-55 overflow-auto rounded-md border border-line-strong bg-bg-3"
                    >
                        <div
                            v-for="o in optMatches"
                            :key="o.name"
                            class="flex cursor-pointer gap-2 px-2 py-1.25 text-xs hover:bg-bg-2"
                            @mousedown.prevent="addOpt(o.name)"
                        >
                            <span class="font-mono tabular-nums">-{{ o.name }}</span
                            ><span class="truncate text-fg-2"> {{ o.help }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>
